/**
 * A programme before its record lands, in the editor's own frame: the header
 * card, the surface's real section tabs, and the surface's layout in bones.
 *
 * @module router/programme-editor/ProgrammeSkeleton
 */

import React from "react";

import { GovernanceBones } from "@/components/governance/GovernanceBones";
import { ModuleEditorSkeleton } from "@/components/studio/ModuleEditorSkeleton";
import { LinkTabs, Panel } from "@/components/ui";
import { Bone, FormPanelBones, SkeletonTable } from "@/components/ui/SkeletonParts";

import { type SurfaceDef, SURFACES } from "./surfaces";

/** `/programmes/<id>` or a change case's base, and the surface the address names. */
export function programmeAddress(
  pathname: string,
): { base: string; surface: SurfaceDef | undefined } | null {
  const match = /^(\/programmes\/(?!new(?:\/|$))[^/]+(?:\/changes\/[^/]+)?)(?:\/([^/]+))?/.exec(
    pathname,
  );
  if (!match) {
    return null;
  }
  return { base: match[1]!, surface: SURFACES.find((surface) => surface.segment === match[2]) };
}

/** The editor bar: title, readiness and save state, then its actions. */
function HeaderSkeleton(): React.ReactElement {
  return (
    <Panel flush className="px-4 py-3">
      <div aria-hidden="true" className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Bone className="h-6 w-2/3 max-w-md" />
          <div className="flex flex-wrap gap-3">
            <Bone className="h-4 w-24" />
            <Bone className="h-4 w-48" />
            <Bone className="h-4 w-28" />
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Bone className="h-8 w-24" />
          <Bone className="size-8" />
        </div>
      </div>
    </Panel>
  );
}

/** Module Studio: the module list beside the open module. */
function StudioSkeleton({
  base,
  pathname,
}: {
  base: string;
  pathname: string;
}): React.ReactElement {
  const moduleId = pathname.slice(base.length).split("/")[2];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]">
      <Panel flush className="flex flex-col gap-2 p-3">
        {Array.from({ length: 6 }, (_, index) => (
          <div
            key={`module-${String(index)}`}
            aria-hidden="true"
            className="flex flex-col gap-2 rounded-box p-3"
          >
            <Bone className="h-4 w-4/5" />
            <Bone className="h-4 w-16" />
          </div>
        ))}
      </Panel>
      <Panel flush className="p-4">
        <ModuleEditorSkeleton
          tabBase={moduleId ? `${base}/studio/${moduleId}` : `${base}/studio`}
        />
      </Panel>
    </div>
  );
}

/** Governance: its panel, with the stages still to be evaluated. */
function GovernanceSkeleton(): React.ReactElement {
  return (
    <Panel
      tone="inset"
      headingLevel={2}
      title="Governance"
      description={<Bone className="mt-1 h-4 w-96 max-w-full" />}
    >
      <GovernanceBones />
    </Panel>
  );
}

/** History: its filter bar over a column of events. */
function HistorySkeleton(): React.ReactElement {
  return (
    <Panel tone="inset" headingLevel={2} title="History">
      <div className="space-y-3">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={`event-${String(index)}`} aria-hidden="true" className="space-y-2">
            <Bone className="h-4 w-2/3" />
            <Bone className="h-3 w-1/3" />
          </div>
        ))}
      </div>
    </Panel>
  );
}

/** Programme Team: its state badges, its two tabs, and the people table. */
function TeamSkeleton(): React.ReactElement {
  return (
    <div aria-hidden="true" className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Bone className="h-6 w-36" />
        <Bone className="h-6 w-24" />
      </div>
      <Panel flush className="flex flex-col gap-4 p-4">
        <div className="flex gap-4 border-b border-base-300 pb-2">
          <Bone className="h-4 w-14" />
          <Bone className="h-4 w-16" />
        </div>
        <SkeletonTable columns={6} rows={3} />
      </Panel>
    </div>
  );
}

function SurfaceSkeleton({
  base,
  pathname,
  surface,
}: {
  base: string;
  pathname: string;
  surface: SurfaceDef | undefined;
}): React.ReactElement {
  if (surface?.key === "studio") {
    return <StudioSkeleton base={base} pathname={pathname} />;
  }
  if (surface?.key === "governance") {
    return <GovernanceSkeleton />;
  }
  if (surface?.key === "history") {
    return <HistorySkeleton />;
  }
  if (surface?.key === "team") {
    return <TeamSkeleton />;
  }
  const sections = surface?.sections ?? SURFACES[0]!.sections;
  const segment = surface?.segment ?? SURFACES[0]!.segment;
  return (
    <div className="flex flex-col gap-4">
      <LinkTabs
        oneRow
        aria-label={`${surface?.title ?? SURFACES[0]!.title} sections`}
        items={sections.map((section) => ({
          to: section.segment ? `${base}/${segment}/${section.segment}` : `${base}/${segment}`,
          label: section.title,
          end: !section.segment,
        }))}
      />
      <FormPanelBones />
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <ProgrammeSkeleton pathname={pathname} label="Opening the programme…" />
 * ```
 */
export function ProgrammeSkeleton({
  pathname,
  label,
  "data-testid": testId,
}: {
  pathname: string;
  /** Read, not drawn: names what is loading. */
  label: string;
  "data-testid"?: string;
}): React.ReactElement {
  const address = programmeAddress(pathname);
  return (
    <div role="status" aria-live="polite" className="space-y-4" data-testid={testId}>
      <span className="sr-only">{label}</span>
      <HeaderSkeleton />
      <SurfaceSkeleton
        base={address?.base ?? pathname}
        pathname={pathname}
        surface={address?.surface}
      />
    </div>
  );
}
